import { useAsyncEffect } from '@xylabs/sdk-react'
import { AuctionStatus, XyoWorldAuction } from '@xyo-network/sdk-xyoworld-js'
import { PropsWithChildren, useEffect, useState } from 'react'

//import { XyoLog } from '../../../lib'
import useMinter from '../useMinter'
import {
  getSortByBidAuctions,
  getSortByEndAuctions,
  getSortByQuadkeyAuctions,
  SortableAuction,
  sortAuctionsBidHighToLow,
  sortAuctionsBidLowToHigh,
  sortAuctionsEndsLatestFirst,
  sortAuctionsEndsSoonestFirst,
  sortAuctionsQuadkeyHighToLow,
  sortAuctionsQuadkeyLowToHigh,
} from './AuctionSorting'
import { AuctionSortOrder } from './AuctionSortOrder'
import { MinterAuctionsContext } from './Context'

interface Props {
  defaultFilter?: AuctionStatus
  enabled?: boolean
}

const MinterAuctionsProvider: React.FC<PropsWithChildren<Props>> = (props) => {
  const { children, defaultFilter, enabled = true } = props
  const { minter } = useMinter()

  //XyoLog.debug(`MinterAuctionProvider [minter:${!!minter}, defaultFilter: ${!!defaultFilter}]`)

  const [filter, setFilter] = useState(defaultFilter)
  const [auctions, setAuctions] = useState<XyoWorldAuction[]>()
  const [sortOrder, setSortOrder] = useState(AuctionSortOrder.EndSoonestFirst)
  const [sortedAuctions, setSortedAuctions] = useState<SortableAuction[]>()
  const [updateCount, setUpdateCount] = useState(0)

  const updateFilter = (status: AuctionStatus) => {
    if (filter !== status) {
      setAuctions(undefined)
      setFilter(status)
      switch (status) {
        case AuctionStatus.Available:
          setSortOrder(AuctionSortOrder.BidHighToLow)
          break
        case AuctionStatus.Live:
          setSortOrder(AuctionSortOrder.EndSoonestFirst)
          break
        case AuctionStatus.Dutch:
          setSortOrder(AuctionSortOrder.BidLowToHigh)
          break
        case AuctionStatus.Completed:
          setSortOrder(AuctionSortOrder.BidHighToLow)
          break
        case AuctionStatus.Upcoming:
          setSortOrder(AuctionSortOrder.QuadkeyLowToHigh)
          break
        case AuctionStatus.Minted:
          setSortOrder(AuctionSortOrder.QuadkeyHighToLow)
          break
      }
    }
  }

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isMounted) => {
      let auctions = undefined
      if (filter && minter) {
        auctions = await minter.listAuctions(filter)
      }
      if (isMounted()) {
        setAuctions(auctions)
      }
    },
    [filter, minter, sortOrder, updateCount, enabled],
  )

  const updateSortOrder = (order: AuctionSortOrder) => {
    setSortedAuctions([]) //this causes the consumers to clear their lists while busy
    if (sortOrder !== order) {
      setSortOrder(order)
    }
  }

  useEffect(() => {
    let sortedAuctions
    if (auctions) {
      switch (sortOrder) {
        case AuctionSortOrder.BidHighToLow:
          sortedAuctions = sortAuctionsBidHighToLow(getSortByBidAuctions(auctions))
          break
        case AuctionSortOrder.BidLowToHigh:
          sortedAuctions = sortAuctionsBidLowToHigh(getSortByBidAuctions(auctions))
          break
        case AuctionSortOrder.EndLatestFirst:
          sortedAuctions = sortAuctionsEndsLatestFirst(getSortByEndAuctions(auctions))
          break
        case AuctionSortOrder.EndSoonestFirst:
          sortedAuctions = sortAuctionsEndsSoonestFirst(getSortByEndAuctions(auctions))
          break
        case AuctionSortOrder.QuadkeyHighToLow:
          sortedAuctions = sortAuctionsQuadkeyHighToLow(getSortByQuadkeyAuctions(auctions))
          break
        case AuctionSortOrder.QuadkeyLowToHigh:
          sortedAuctions = sortAuctionsQuadkeyLowToHigh(getSortByQuadkeyAuctions(auctions))
          break
      }
      setSortedAuctions(sortedAuctions)
    }
  }, [sortOrder, auctions, updateCount, enabled])

  const update = async () => {
    setSortedAuctions(undefined)
    setAuctions(undefined)
    await minter?.updateWithNewEvents()
    setUpdateCount(updateCount + 1) //force effects to run
  }

  const value = {
    auctions,
    filter,
    name: 'MinterAuctionsContext',
    sortOrder,
    sortedAuctions,
    update,
    updateFilter,
    updateSortOrder,
  }

  return <MinterAuctionsContext.Provider value={value}>{children}</MinterAuctionsContext.Provider>
}

export default MinterAuctionsProvider
